/**
 * The configuration's summary: its name, the price with the list price crossed out and the saving, the offers that come
 * with it (each explained in our words), the one lime action that starts the buy flow, and the payment methods the
 * sandbox really takes, as plain words. On a phone a slim bar with the price and the action stays above the tab bar.
 */
import { cn } from "@prop/ui";
import { Check, Gift, RotateCcw, Sparkles } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useConfigT } from "../../../i18n";
import { fmtUsd } from "../../../lib/fmt";
import { buyHref, offerKeys, percentOff, saving, sizeLabel } from "./model";
import { BuyBody } from "../Buy";
import { Glide, Swap } from "./motion";
import type { Config } from "./useConfigurator";

function useStart(c: Config) {
  const [, navigate] = useLocation();
  return () => {
    if (c.program) navigate(buyHref(c.program, c.choices, c.offer));
  };
}

/**
 * The limited offer: the sale is already in the price (a lime note), and the buyer picks one bonus — the extra accounts
 * or the free reset (owner, 2026-10-30, ADR-0035). The ink fill glides to the picked card; the card says it in a line,
 * and the full text shows only on its "?".
 */
function OfferChips({ c }: { c: Config }) {
  const t = useConfigT();
  const off = percentOff(c.price?.priceUsd ?? null, c.price?.listPriceUsd ?? null);
  const o = c.program?.offers;
  const cards = offerKeys(o).map((k) =>
    k === "extraAccounts"
      ? { k, Icon: Gift, label: t("summary.buyGet", { n: o?.extraAccounts ?? 0 }), short: t("tips.buyGetShort", { n: o?.extraAccounts ?? 0 }), text: t("tips.buyGet", { n: o?.extraAccounts ?? 0 }) }
      : { k, Icon: RotateCcw, label: t("summary.resets", { n: o?.freeResets ?? 0 }), short: t("tips.resetShort"), text: t("tips.reset") },
  );
  if (!off && !cards.length) return null;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">{t("summary.offer")}</span>
        {off && (
          <span className="cfg-offer-tag inline-flex items-center gap-1.5 rounded-full bg-[#d4ff24] px-2.5 py-1 text-xs font-bold text-[#030407]" title={t("tips.off", { v: off })}>
            <Sparkles className="size-3.5" aria-hidden />
            {t("summary.off", { v: off })} · {t("summary.offNote")}
          </span>
        )}
      </div>
      {cards.length > 0 && (
        <>
          <span className="text-xs text-muted-foreground">{t("summary.pick")}</span>
          <div className="grid gap-2" role="radiogroup" aria-label={t("summary.pick")}>
            {cards.map(({ k, Icon, label, short, text }) => {
              const picked = c.offer === k;
              return (
                <div key={k} className="relative">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={picked}
                    onClick={() => c.setOffer(k)}
                    className={cn(
                      "relative isolate flex h-full w-full flex-col items-start gap-1 rounded-[12px] border px-3 py-2.5 text-left transition-[border-color,color,transform,scale] duration-200 hover:-translate-y-0.5 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      picked ? "border-transparent text-background" : "border-foreground/15 bg-foreground/[0.05] hover:border-foreground/45",
                    )}
                  >
                    {picked && <Glide group="offer" className="glide rounded-[inherit]" />}
                    <span className="relative z-10 flex w-full items-start gap-2">
                      <Icon className={cn("size-4 shrink-0", picked ? "text-[#4d7c00]" : "text-[#d4ff24]")} aria-hidden />
                      <span className="min-w-0 text-xs font-bold leading-tight tracking-wide">{label}</span>
                      <span className={cn("ml-auto grid size-4 shrink-0 place-items-center rounded-full border transition-colors", picked ? "border-[#d4ff24] bg-[#d4ff24] text-[#030407]" : "border-foreground/30")}>
                        {picked && <Check className="size-3" strokeWidth={3} aria-hidden />}
                      </span>
                    </span>
                    <span className={cn("relative z-10 pr-6 text-xs leading-snug", picked ? "opacity-75" : "text-muted-foreground")}>{short}</span>
                  </button>
                  {/* What the bonus is: only while the pointer is on the "?" itself (or it has keyboard focus). */}
                  <span className="group/q absolute bottom-2 right-2 z-20">
                    <button
                      type="button"
                      aria-label={text}
                      className={cn(
                        "grid size-5 place-items-center rounded-full border text-[10px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        picked ? "border-background/35 text-background/70 hover:text-background" : "border-foreground/25 text-muted-foreground hover:border-foreground/60 hover:text-foreground",
                      )}
                    >
                      ?
                    </button>
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute bottom-[calc(100%+8px)] right-0 w-64 translate-y-1 rounded-[12px] bg-foreground p-3 text-xs leading-snug text-background opacity-0 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.6)] transition-[opacity,transform] duration-200 group-hover/q:translate-y-0 group-hover/q:opacity-100 group-focus-within/q:translate-y-0 group-focus-within/q:opacity-100"
                    >
                      {text}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function SummaryCard({ c, rulesHref = true }: { c: Config; rulesHref?: boolean }) {
  const t = useConfigT();
  const start = useStart(c);
  const p = c.program;
  const pr = c.price;
  const save = saving(pr?.priceUsd ?? null, pr?.listPriceUsd ?? null);
  if (!p || !c.family) return null;
  return (
    <section className="cfg-glow flex flex-col gap-4 rounded-[14px] bg-card p-5" aria-labelledby="cfg-sum">
      <div className="flex flex-col gap-1">
        <h3 id="cfg-sum" className="font-display text-xl font-semibold uppercase leading-tight tracking-tight [font-stretch:120%]">
          <Swap value={`${c.family.name} ${c.size}`}>
            {c.family.name} <span className="ml-1.5 whitespace-nowrap">{sizeLabel(c.size)}</span>
          </Swap>
        </h3>
        <p className="text-sm text-muted-foreground">
          {t(`product.${c.product}`)} · {t(`evaluation.${c.evaluation}`)}
        </p>
      </div>
      <div className="flex flex-col gap-2 border-t border-border pt-5">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
          <p className="font-display text-[clamp(2.25rem,10vw,3rem)] font-semibold leading-none tracking-tight tabular-nums [font-stretch:120%]">
            <Swap value={pr?.priceUsd ?? "—"}>{pr?.priceUsd ? fmtUsd(pr.priceUsd) : "—"}</Swap>
          </p>
          {save && pr?.listPriceUsd && <span className="pb-1 text-lg tabular-nums text-muted-foreground line-through">{fmtUsd(pr.listPriceUsd)}</span>}
        </div>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {save && <span className="rounded-full bg-foreground px-2 py-0.5 text-xs font-semibold text-background tabular-nums">{t("summary.save", { v: fmtUsd(save) })}</span>}
          <span className="text-muted-foreground">
            {t("summary.currency")} · {t("summary.oneTime")}
          </span>
        </p>
        {pr?.activationFeeUsd && (
          <p className="text-sm tabular-nums">
            {t("summary.whenFunded", { fee: fmtUsd(pr.activationFeeUsd) })}
            {pr.activationFeeListUsd && pr.activationFeeListUsd !== pr.activationFeeUsd && <span className="ml-1.5 text-muted-foreground line-through">{fmtUsd(pr.activationFeeListUsd)}</span>}
          </p>
        )}
      </div>
      <OfferChips c={c} />
      <button type="button" className="buy buy-full" onClick={start} disabled={!pr?.priceUsd}>
        <BuyBody>{t("summary.start")}</BuyBody>
      </button>
      <p className="text-xs leading-relaxed text-muted-foreground">{t("summary.payWith")}</p>
      {rulesHref && (
        <Link href={`/programs/${p.slug}`} className="self-start text-sm underline underline-offset-4">
          {t("summary.fullRules")}
        </Link>
      )}
    </section>
  );
}

/** Phone and tablet: price and the action pinned at the bottom (above the app's tab bar where there is one). */
export function StickyBar({ c, bottom }: { c: Config; bottom: string }) {
  const t = useConfigT();
  const start = useStart(c);
  const pr = c.price;
  if (!c.program || !c.family) return null;
  return (
    <div data-bottom-bar className={cn("sticky z-10 -mx-4 mt-2 flex items-center gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur lg:hidden", bottom)}>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline gap-2">
          <Swap value={pr?.priceUsd ?? "—"} className="text-xl font-semibold tabular-nums">
            {pr?.priceUsd ? fmtUsd(pr.priceUsd) : "—"}
          </Swap>
          {pr?.listPriceUsd && pr.listPriceUsd !== pr.priceUsd && <span className="text-sm tabular-nums text-muted-foreground line-through">{fmtUsd(pr.listPriceUsd)}</span>}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {c.family.name} {sizeLabel(c.size)}
        </span>
      </div>
      <button type="button" className="buy sm shrink-0" onClick={start} disabled={!pr?.priceUsd}>
        <BuyBody>{t("summary.start")}</BuyBody>
      </button>
    </div>
  );
}
